<template>
  <div
    class="rounded-medium focus-within:border-outline-variant group flex min-h-8 w-full items-center gap-1 border border-transparent py-1 pr-1"
    :class="[numberOfActions < 2 && 'pl-1']"
  >
    <div class="flex flex-row items-center self-start" :class="[isEditable && 'mt-1']">
      <span
        v-if="isEditable && numberOfActions > 1"
        class="action-drag-handle text-on-surface-variant mt-0.5 inline-flex cursor-grab touch-none px-0.5 select-none active:cursor-grabbing"
      >
        <BaseIcon size="20">drag_indicator</BaseIcon>
      </span>
      <button
        type="button"
        class="mt-0.5 mr-2 shrink-0"
        :class="[!isEditable && 'cursor-auto']"
        :disabled="!isEditable"
        @click="emits('setChecked', !action.isChecked)"
      >
        <BaseCheckbox :is-checked="action.isChecked" :class="[!isEditable && 'cursor-auto']" />
      </button>
    </div>
    <textarea
      v-model="newTitle"
      ref="textarea"
      class="min-w-0 grow resize-none outline-none"
      :disabled="action.isChecked || !isEditable"
      :autofocus="autofocus"
      :class="[action.isChecked && 'text-on-surface-variant line-through']"
      @keydown="onTitleKeydown"
    />
    <div v-if="isEditable" class="flex flex-row items-start self-start">
      <template v-if="titleHasChanged">
        <BaseTextButton icon="save" size="XS" @click="emits('rename', newTitle)">{{
          $t('common.save')
        }}</BaseTextButton>
        <BaseIconButton
          v-tooltip="$t('common.cancel')"
          icon="close"
          size="XS"
          @click="undoTitleChange"
        />
      </template>
      <BaseIconButton
        v-else
        v-tooltip="$t('common.delete')"
        icon="delete"
        size="XS"
        @click="emits('delete')"
        class="invisible group-hover:visible"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ObjectiveAction } from '@/core/composables/use-objectives'
  import BaseCheckbox from '../base/checkbox/BaseCheckbox.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import { computed, nextTick, watch } from 'vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import { useTextareaAutosize } from '@vueuse/core'

  interface Props {
    action: ObjectiveAction
    isEditable: boolean
    autofocus?: boolean
    numberOfActions: number
  }

  const props = defineProps<Props>()

  const titleHasChanged = computed(() => newTitle.value !== props.action.title)

  const { textarea, input: newTitle } = useTextareaAutosize()
  newTitle.value = props.action.title

  watch(
    () => props.autofocus,
    (shouldFocus) => {
      if (!shouldFocus) {
        return
      }
      void nextTick(() => {
        textarea.value?.focus({ preventScroll: true })
      })
    },
    { immediate: true, flush: 'post' },
  )

  const emits = defineEmits<{
    (e: 'setChecked', isChecked: boolean): void
    (e: 'rename', title: string): void
    (e: 'delete'): void
    (e: 'dirty-changed', dirty: boolean): void
  }>()

  watch(
    titleHasChanged,
    (dirty) => {
      emits('dirty-changed', dirty)
    },
    { immediate: true },
  )

  const saveTitle = () => {
    if (!titleHasChanged.value) {
      return
    }
    emits('rename', newTitle.value)
  }

  const onTitleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      undoTitleChange()
      return
    }
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
      return
    }
    event.preventDefault()
    saveTitle()
  }

  const undoTitleChange = () => {
    newTitle.value = props.action.title
  }
</script>
